import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { ModuleId } from "@/modules";
import type { ChatPendingMessage } from "@/features/chat";
import { isDraftChatKey } from "@/features/chat/store/streaming-store";
import {
  clampDockWidth,
  DOCK_DEFAULT_WIDTH,
} from "../constants";

interface ChatDockStore {
  /** Desktop tercihi — persist edilir, modüle dönünce kolon geri gelir. */
  isOpen: boolean;
  /**
   * Mobil sheet oturumu — persist edilmez. Modüle girince kapalı başlar;
   * tutamaç / kısayol / sohbet seçimi açar.
   */
  mobileOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;

  /** Desktop kolon genişliği (px). Varsayılan sabit ölçü; kullanıcı sürükleyerek açabilir. */
  width: number;
  setWidth: (width: number) => void;

  /**
   * Modül → dock'ta açık konuşma. Bu bir UI seçimi (hangi sohbete
   * bakılıyor), sohbetin modülü değil — o sunucuda (`conversation.module`).
   * Cihaza özel kalması bilinçli; persist edilir.
   */
  chatByModule: Partial<Record<ModuleId, string>>;
  setModuleChat: (moduleId: ModuleId, chatId: string | null) => void;

  /** Yeni oluşturulan konuşmanın ilk mesajı; StreamingChat tüketir. Persist edilmez. */
  pending: ChatPendingMessage | null;
  setPending: (pending: ChatPendingMessage | null) => void;

  /** Taslak tur bağlanmadan biterse composer'a geri yazılır. Persist edilmez. */
  composerRestore: { message: string; attachments?: ChatPendingMessage["attachments"] } | null;
  setComposerRestore: (
    value: { message: string; attachments?: ChatPendingMessage["attachments"] } | null,
  ) => void;
}

/**
 * Sağ chat dock'unun durumu. Sol sidebar'dan bağımsızdır; SidebarProvider'ın
 * right state'i mobil/left ile iç içe olduğu ve persist edilmediği için
 * kullanılmıyor.
 */
export const useChatDockStore = create<ChatDockStore>()(
  persist(
    (set) => ({
      isOpen: false,
      mobileOpen: false,
      open: () => set({ isOpen: true, mobileOpen: true }),
      close: () => set({ isOpen: false, mobileOpen: false }),
      toggle: () =>
        set((s) => {
          const next = !s.isOpen;
          return { isOpen: next, mobileOpen: next };
        }),

      width: DOCK_DEFAULT_WIDTH,
      setWidth: (width) => set({ width: clampDockWidth(width) }),

      chatByModule: {},
      setModuleChat: (moduleId, chatId) =>
        set((s) => {
          const next = { ...s.chatByModule };
          if (chatId) next[moduleId] = chatId;
          else delete next[moduleId];
          return { chatByModule: next };
        }),

      pending: null,
      setPending: (pending) => set({ pending }),

      composerRestore: null,
      setComposerRestore: (composerRestore) => set({ composerRestore }),
    }),
    {
      name: "chat-dock-storage",
      partialize: (s) => ({
        isOpen: s.isOpen,
        chatByModule: Object.fromEntries(
          Object.entries(s.chatByModule).filter(([, id]) => !isDraftChatKey(id)),
        ),
        width: s.width,
      }),
      merge: (persisted, current) => {
        const stored = persisted as Partial<ChatDockStore> | undefined;
        return {
          ...current,
          ...stored,
          width: clampDockWidth(stored?.width ?? current.width),
        };
      },
    },
  ),
);
